import React, {useState, useEffect} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import {useMutation, useQuery} from '@apollo/client';
import {signInWithCustomToken} from 'firebase/auth';
import {auth} from '../config/firebase';
import {
  VALIDATE_JOURNEY_MAGIC_LINK,
  CONSUME_JOURNEY_MAGIC_LINK,
} from '../graphql/queries';
import logo from '../images/logo.png';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

type JoinState = 'loading' | 'valid' | 'invalid' | 'joining' | 'success' | 'error';

export default function JourneyJoinPage() {
  useDocumentTitle('Join Journey');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState<JoinState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [journeyInfo, setJourneyInfo] = useState<{
    planId?: string;
    processTitle?: string;
    applicantEmail?: string;
    roles?: Array<{
      roleId: string;
      name: string;
      description?: string;
      assigneeName?: string;
      assigneeEmail?: string;
    }>;
  }>({});

  const [autoConsume, setAutoConsume] = useState(false);

  // Validate magic link on mount
  const {loading: validating} = useQuery(VALIDATE_JOURNEY_MAGIC_LINK, {
    variables: {token: token || ''},
    skip: !token,
    onCompleted: data => {
      if (data.validateJourneyMagicLink.valid) {
        const roles = data.validateJourneyMagicLink.roles || [];
        setJourneyInfo({
          planId: data.validateJourneyMagicLink.planId,
          processTitle: data.validateJourneyMagicLink.processTitle,
          applicantEmail: data.validateJourneyMagicLink.applicantEmail,
          roles,
        });
        setState('valid');

        // Auto-consume for preview-claimed plans (single "Applicant" role = bare-bones plan).
        // The full plan is being generated in the background; skip the role preview.
        if (roles.length <= 1) {
          setAutoConsume(true);
        }
      } else {
        setState('invalid');
        setErrorMessage(
          data.validateJourneyMagicLink.error || 'Invalid or expired link'
        );
      }
    },
    onError: err => {
      setState('error');
      setErrorMessage(err.message || 'Failed to validate link');
    },
  });

  const [consumeMagicLink] = useMutation(CONSUME_JOURNEY_MAGIC_LINK);

  useEffect(() => {
    if (!token) {
      setState('invalid');
      setErrorMessage('No journey token provided');
    } else if (validating) {
      setState('loading');
    }
  }, [token, validating]);

  // Auto-consume for preview-claimed plans (bare-bones → skip role preview)
  useEffect(() => {
    if (autoConsume && state === 'valid' && token) {
      handleViewJourney();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConsume, state, token]);

  const handleViewJourney = async () => {
    if (!token) return;

    setState('joining');
    setErrorMessage('');

    try {
      const {data} = await consumeMagicLink({variables: {token}});

      if (!data.consumeJourneyMagicLink.valid) {
        setState('error');
        setErrorMessage(
          data.consumeJourneyMagicLink.error || 'Failed to access journey'
        );
        return;
      }

      const firebaseToken = data.consumeJourneyMagicLink.firebaseToken;
      if (!firebaseToken) {
        setState('error');
        setErrorMessage('No authentication token received');
        return;
      }

      await signInWithCustomToken(auth, firebaseToken);

      setState('success');

      setTimeout(() => {
        navigate(`/app/plans/${data.consumeJourneyMagicLink.planId}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error accessing journey:', error);
      setState('error');
      setErrorMessage(error.message || 'Failed to access journey');
    }
  };

  // Loading
  if (state === 'loading') {
    return (
      <Container maxWidth="sm" sx={{py: 8}}>
        <Card>
          <CardContent sx={{py: 4}}>
            <Box sx={{textAlign: 'center', mb: 4}}>
              <img
                src={logo}
                alt="Logo"
                style={{height: '50px', width: 'auto', marginBottom: '16px'}}
              />
            </Box>
            <Box sx={{px: 2}}>
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={80} sx={{my: 2}} />
              <Skeleton variant="text" height={30} />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Invalid/Error
  if (state === 'invalid' || state === 'error') {
    return (
      <Container maxWidth="sm" sx={{py: 8}}>
        <Card>
          <CardContent sx={{py: 4}}>
            <Box sx={{textAlign: 'center', mb: 3}}>
              <img
                src={logo}
                alt="Logo"
                style={{height: '50px', width: 'auto', marginBottom: '16px'}}
              />
            </Box>
            <Box sx={{textAlign: 'center', mb: 3}}>
              <ErrorIcon sx={{fontSize: 60, color: 'error.main', mb: 2}} />
              <Typography variant="h5" gutterBottom>
                {state === 'invalid'
                  ? 'Invalid Link'
                  : 'Something Went Wrong'}
              </Typography>
            </Box>
            <Alert severity="error" sx={{mb: 3}}>
              {errorMessage}
            </Alert>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{mb: 3, textAlign: 'center'}}
            >
              This link may have expired or already been used. Please check
              your email for a new link.
            </Typography>
            <Box sx={{textAlign: 'center'}}>
              <Button variant="outlined" onClick={() => navigate('/')}>
                Go to Home
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Success
  if (state === 'success') {
    return (
      <Container maxWidth="sm" sx={{py: 8}}>
        <Card>
          <CardContent sx={{py: 4}}>
            <Box sx={{textAlign: 'center'}}>
              <CheckCircleIcon
                sx={{fontSize: 80, color: 'success.main', mb: 2}}
              />
              <Typography variant="h5" gutterBottom>
                Welcome!
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{mb: 2}}
              >
                You now have access to your journey.
              </Typography>
              <CircularProgress size={24} sx={{mt: 2}} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{mt: 1}}
              >
                Redirecting to your journey...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Valid - show preview and "View Your Journey" button
  return (
    <Container maxWidth="sm" sx={{py: 8}}>
      <Card>
        <CardContent sx={{py: 4}}>
          <Box sx={{textAlign: 'center', mb: 4}}>
            <img
              src={logo}
              alt="Logo"
              style={{height: '50px', width: 'auto', marginBottom: '16px'}}
            />
          </Box>

          <Typography variant="h5" gutterBottom sx={{textAlign: 'center'}}>
            Your Application Journey
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{textAlign: 'center', mb: 3}}
          >
            You've been invited to view your journey:
          </Typography>

          <Card variant="outlined" sx={{mb: 4, bgcolor: 'grey.50'}}>
            <CardContent>
              <Typography variant="h6" sx={{fontWeight: 600}}>
                {journeyInfo.processTitle}
              </Typography>
              {journeyInfo.applicantEmail && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{mt: 0.5}}
                >
                  {journeyInfo.applicantEmail}
                </Typography>
              )}

              {/* Roles */}
              {journeyInfo.roles && journeyInfo.roles.length > 0 && (
                <Box sx={{mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200'}}>
                  <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1, color: 'text.secondary'}}>
                    Roles
                  </Typography>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                    {journeyInfo.roles.map((role) => (
                      <Box
                        key={role.roleId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <PersonIcon sx={{fontSize: 18, color: 'text.secondary'}} />
                        <Typography variant="body2" sx={{fontWeight: 600}}>
                          {role.name}
                        </Typography>
                        {(role.assigneeName || role.assigneeEmail) ? (
                          <Chip
                            label={role.assigneeName || role.assigneeEmail}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{height: 22, fontSize: '0.75rem'}}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{mb: 3, textAlign: 'center'}}
          >
            Click the button below to view your steps and track your progress.
          </Typography>

          <Box sx={{textAlign: 'center'}}>
            <Button
              variant="contained"
              size="large"
              onClick={handleViewJourney}
              disabled={state === 'joining'}
              sx={{minWidth: 200}}
            >
              {state === 'joining' ? (
                <>
                  <CircularProgress
                    size={20}
                    sx={{mr: 1}}
                    color="inherit"
                  />
                  Loading...
                </>
              ) : (
                'View Your Journey'
              )}
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{display: 'block', textAlign: 'center', mt: 3}}
          >
            This link is unique to you. Please don't share it with others.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
}
